import { useFieldArray, type FieldErrors, type Control } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { ControlledTextField, SimpleButton, ErrorMessage, ControlledSwitch } from '@/src/ui';
import type { QtiGraphingQuestionDTO } from '@/src/lib';

interface GraphPointsEditorProps {
    question: QtiGraphingQuestionDTO;
    control: Control<QtiGraphingQuestionDTO>;
    errors: FieldErrors<QtiGraphingQuestionDTO>;
}

export default function GraphPointsEditor({ question, control, errors }: GraphPointsEditorProps) {
    const minPoints = 0;
    const maxPoints = 10;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'points',
    });

    const handleAdd = () => {
        if (fields.length >= maxPoints) return;

        let nextIndex = 1;
        while (fields.find((field) => field.label === `P${nextIndex}`)) {
            nextIndex++;
        }
        append({
            id: uuidv4(),
            label: `P${nextIndex}`,
            x: 0,
            y: 0,
            isHidden: true,
        });
    };

    return (
        <>
            {fields.length === 0 && (
                <div className="text-sm text-gray-500">
                    No points yet. Click <b>Add Point</b> to begin.
                </div>
            )}

            {fields.map((field, index) => (
                <div
                    key={field.id}
                    className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between border border-gray-300 rounded bg-gray-50 p-3 text-sm mb-2"
                >
                    <div className="flex flex-col gap-3 md:flex-row md:flex-wrap">
                        <ControlledTextField
                            name={`points.${index}.label`}
                            control={control}
                            label="Label"
                            placeholder="e.g. A"
                            error={errors.points?.[index]?.label}
                        />

                        <ControlledTextField
                            name={`points.${index}.x`}
                            control={control}
                            label="X"
                            type="number"
                            inputProps={{ step: 0.1 }}
                            error={errors.points?.[index]?.x}
                        />

                        <ControlledTextField
                            name={`points.${index}.y`}
                            control={control}
                            label="Y"
                            type="number"
                            inputProps={{ step: 0.1 }}
                            error={errors.points?.[index]?.y}
                        />

                        <ControlledSwitch
                            name={`points.${index}.isHidden`}
                            control={control}
                            label={
                                question.points[index]?.isHidden
                                    ? 'Hidden (Scored)'
                                    : 'Visible (Not Scored)'
                            }
                        />
                    </div>

                    <div className="flex gap-1 pt-1">
                        <SimpleButton
                            type="button"
                            color="error"
                            variant="outlined"
                            disabled={fields.length <= minPoints}
                            onClick={() => remove(index)}
                        >
                            Remove
                        </SimpleButton>
                    </div>

                    <ErrorMessage error={errors.points?.[index] as any} />
                </div>
            ))}

            <ErrorMessage error={errors.points?.root} />
            <ErrorMessage error={errors.points} />

            <SimpleButton
                type="button"
                color="primary"
                onClick={handleAdd}
                className="bg-blue-500 text-white px-4 py-2 rounded"
                disabled={fields.length >= maxPoints}
            >
                Add Point
            </SimpleButton>
        </>
    );
}
